import { useQuery } from '@tanstack/react-query';

import { type Dref, type EGfsLevel, type Fhr, type Tref } from '#entities';
import { REACT_APP_API_FETCH_INTERVAL } from '#shared';

import {
  weatherApi,
  type BinaryWindResponse,
  type PngWindResponse,
} from '../aviationWeather.api';

import type { Dayjs } from 'dayjs';

// export type WindRegion = 130 | 242;

export interface WindsForecastGridSearchParams {
  dref: Dref;
  tref: Tref;
  fhr: Fhr;
  level: EGfsLevel;
  dateTime?: Dayjs;
}

export const windsForecastGridQueryKey = (
  params: WindsForecastGridSearchParams,
) => ['weather', 'gfs-winds-forecast-grid', params];

export const windsForecastGridQueryFn = async (
  params: WindsForecastGridSearchParams,
) => await weatherApi.getWindsForecastGrid(params);

export const useQueryWindsForecastGrid = (
  params: WindsForecastGridSearchParams,
  disabled = false,
) => {
  const enabled =
    !Object.values(params).includes(v => v === undefined) && !disabled;
  return useQuery({
    queryKey: windsForecastGridQueryKey(params),
    queryFn: async () => windsForecastGridQueryFn(params),
    enabled,
    staleTime: 60 * 60 * 1000,
    refetchInterval: 1000 * REACT_APP_API_FETCH_INTERVAL,
    refetchIntervalInBackground: true,
  });
};

// ─── Binary variant ───────────────────────────────────────────────────────────

export const windsForecastGridBinaryQueryKey = (
  params: WindsForecastGridSearchParams,
) => ['weather', 'gfs-winds-forecast-grid-binary', params];

export const windsForecastGridBinaryQueryFn = async (
  params: WindsForecastGridSearchParams,
): Promise<BinaryWindResponse> => weatherApi.getWindsForecastGridBinary(params);

export const useQueryWindsForecastGridBinary = (
  params: WindsForecastGridSearchParams,
  disabled = false,
) => {
  const enabled =
    !Object.values(params).includes(v => v === undefined) && !disabled;
  return useQuery({
    queryKey: windsForecastGridBinaryQueryKey(params),
    queryFn: async () => windsForecastGridBinaryQueryFn(params),
    enabled,
    staleTime: 60 * 60 * 1000,
    refetchInterval: 1000 * REACT_APP_API_FETCH_INTERVAL,
    refetchIntervalInBackground: true,
  });
};

// ─── PNG variant ──────────────────────────────────────────────────────────────
// SpecRef: ARCH-002 (primary transport — replaces binary)
// Reference: docs/decisions/ARCH-002-webgl-heatmap-migration.md#react-query-integration-1

export const windsForecastGridPngQueryKey = (
  params: WindsForecastGridSearchParams,
) => ['weather', 'gfs-winds-forecast-grid-png', params];

export const windsForecastGridPngQueryFn = async (
  params: WindsForecastGridSearchParams,
): Promise<PngWindResponse> => weatherApi.getWindsForecastGridPng(params);

export const useQueryWindsForecastGridPng = (
  params: WindsForecastGridSearchParams,
  disabled = false,
) => {
  const enabled =
    !Object.values(params).includes(v => v === undefined) && !disabled;
  return useQuery({
    queryKey: windsForecastGridPngQueryKey(params),
    queryFn: async () => windsForecastGridPngQueryFn(params),
    enabled,
    staleTime: 60 * 60 * 1000,
    refetchInterval: 1000 * REACT_APP_API_FETCH_INTERVAL,
    refetchIntervalInBackground: true,
  });
};
